import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { CaseNav } from "./CaseNav";
import { CaseHeader } from "./CaseHeader";
import { Section } from "./Section";
import { useOptimizedResize } from '../utils/performance';
import { SectionHub } from "./SectionHub";
import UploadProgress from "../components/UploadProgress";
import uploadService from "../services/simpleUploadService";
import { CaseSidebar } from "./CaseSidebar";
import { useNavVariant } from '../App';
import { GenerationReviewProvider } from './GenerationReview';
import { caseReadScope, caseSectionsFromResponse, normalizeCaseReadId } from '../utils/caseReadScope';
import {
    documentFolderSortsEqual,
    normalizeDocumentFolderSort
} from '../utils/documentFolderSort';
import {
    documentFolderRequestIsCurrent,
    documentStructureRequestIsCurrent,
    normalizeDocumentRouteCaseId
} from '../utils/documentStructureRequest';
import { createDocumentLiveUnreadRegistry } from '../utils/documentLiveUpdates';

const JANE_WORKSPACE_PLAN_STORAGE_KEY = 'jane-workspace-plan';

export const Case = ({ user_id }) => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const navVariant = useNavVariant();
    const [caseData, setCaseData] = useState({});
    const [caseLoadError, setCaseLoadError] = useState(null);
    const [sectionLoadError, setSectionLoadError] = useState(null);
    const [sections, setSections] = useState([]);
    const [activeSection, setActiveSection] = useState(1);
    const [sectionHubMode, setSectionHubMode] = useState(false);
    const [sectionHubChildren, setSectionHubChildren] = useState([]);
    const [folders, setFolders] = useState({});
    const [vitals, setVitals] = useState([]);
    const [caseTypes, setCaseTypes] = useState([]);
    const [metaData, setMetaData] = useState([]);
    const [pfs, setPfs] = useState(null);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [activityKey, setActivityKey] = useState(null);
    const [openVitals, setOpenVitals] = useState(false);
    
    // Upload states
    const [uploads, setUploads] = useState([]);
    const [showUploadProgress, setShowUploadProgress] = useState(false);
    const completedCountRef = useRef(0);
    const sectionRef = useRef(null);
    const caseHeaderRef = useRef(null);
    const caseDataRequestGenerationRef = useRef(0);
    const sectionsRequestGenerationRef = useRef(0);
    const vitalsRequestGenerationRef = useRef(0);
    const documentStructureRequestRef = useRef({ generation: 0, controller: null });
    const folderFetchControllersRef = useRef(new Map());
    const activeRouteCaseIdRef = useRef(String(id || ''));
    activeRouteCaseIdRef.current = String(id || '');
    const loadedCaseScope = caseReadScope(caseData, id);
    const loadedCaseScopeRef = useRef(null);
    loadedCaseScopeRef.current = loadedCaseScope;
    const documentLiveUnreadHolderRef = useRef({ caseId: null, registry: null });
    const normalizedRouteCaseId = normalizeDocumentRouteCaseId(id);
    if (documentLiveUnreadHolderRef.current.caseId !== normalizedRouteCaseId) {
        documentLiveUnreadHolderRef.current = {
            caseId: normalizedRouteCaseId,
            registry: createDocumentLiveUnreadRegistry()
        };
    }
    const [generationEditTarget, setGenerationEditTarget] = useState(null);

    const [pendingJaneWorkspacePlan, setPendingJaneWorkspacePlan] = useState(null);

    // Settlement phase tracking
    const [settlementFieldsRequired, setSettlementFieldsRequired] = useState(false);
    const [pendingSettlementPhase, setPendingSettlementPhase] = useState(null);
    const [settlementVerificationNotice, setSettlementVerificationNotice] = useState(null);
    const [settlementActivityDraft, setSettlementActivityDraft] = useState(null);
    const [disengagementFieldsRequired, setDisengagementFieldsRequired] = useState(false);
    const [pendingDisengagementPhase, setPendingDisengagementPhase] = useState(null);
    const [disengagementActivityDraft, setDisengagementActivityDraft] = useState(null);
    const [guidedActivityDraft, setGuidedActivityDraft] = useState(null);
    const [guidedBillingDraft, setGuidedBillingDraft] = useState(null);
    const [guidedSectionDataDraft, setGuidedSectionDataDraft] = useState(null);

    const params = new URLSearchParams(location.search);

    useEffect(() => {
        const isMobileViewport = window.matchMedia?.('(max-width: 767px)').matches || window.innerWidth < 768;
        if (!isMobileViewport) return;

        const scrollToTop = () => {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            document.querySelector('.case-body')?.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
            document.querySelector('.case-section')?.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
        };

        scrollToTop();
        const animationFrame = window.requestAnimationFrame(scrollToTop);
        const timeout = window.setTimeout(scrollToTop, 100);

        return () => {
            window.cancelAnimationFrame(animationFrame);
            window.clearTimeout(timeout);
        };
    }, [id]);

    useOptimizedResize(() => {
        setScreenWidth(window.innerWidth);
    });

    const isSettlementFlowActive = settlementFieldsRequired || pendingSettlementPhase !== null;
    const isDisengagementFlowActive = disengagementFieldsRequired || pendingDisengagementPhase !== null;

    const getSettlementRedirectSection = () => (settlementFieldsRequired ? 20 : 24);
    const getActivityFeedSection = () => sections.find(s => s.name?.toLowerCase() === 'activity feed');

    const clearSettlementFlow = () => {
        setPendingSettlementPhase(null);
        setSettlementFieldsRequired(false);
        setSettlementVerificationNotice(null);
        setSettlementActivityDraft(null);
        window.dispatchEvent(new CustomEvent('clearSettlementState'));
    };

    const clearDisengagementFlow = () => {
        setPendingDisengagementPhase(null);
        setDisengagementFieldsRequired(false);
        setDisengagementActivityDraft(null);
        window.dispatchEvent(new CustomEvent('clearDisengagementState'));
    };

    const guardSettlementNavigation = (targetSectionId) => {
        if (!isSettlementFlowActive && !isDisengagementFlowActive) return true;

        const activitySection = getActivityFeedSection();
        if (isSettlementFlowActive && (targetSectionId === 20 || targetSectionId === 24)) return true;
        if (isDisengagementFlowActive && (targetSectionId === 20 || targetSectionId === activitySection?.id)) return true;

        const confirmNavigate = window.confirm(
            isDisengagementFlowActive
                ? 'Disengagement phase is in progress. Please save/confirm before leaving. Continue to the required section?'
                : 'Settlement phase is in progress. Please save/confirm before leaving. Continue to the required section?'
        );

        if (confirmNavigate) {
            if (isSettlementFlowActive) clearSettlementFlow();
            if (isDisengagementFlowActive) clearDisengagementFlow();
            return true;
        }

        return false;
    };

    const applyActiveSection = (sectionId, subSections = []) => {
        const section = sections.find(s => s.id === sectionId);

        if (!section) return;

        const isStatic = staticSections.some(staticName =>
            staticName.toLowerCase() === section.name.toLowerCase()
        );

        if (subSections.length > 0 && !isStatic) {
            setSectionHubMode(true);
            setSectionHubChildren(subSections);
            setActiveSection(sectionId);
        } else {
            setSectionHubMode(false);
            setActiveSection(sectionId);
        }

        const url = new URL(window.location.href);
        url.searchParams.set("section", section.id);
        
        // Preserve key parameter so Activity Feed can scroll/highlight first
        
        window.history.replaceState({}, "", url);
    };

    const handleSetActiveSection = (sectionId, subSections = []) => {
        if (!guardSettlementNavigation(sectionId)) return;
        applyActiveSection(sectionId, subSections);
    };

    const openSettlementActivityDraft = () => {
        const activitySection = sections.find(s => s.name?.toLowerCase() === 'activity feed');

        setSettlementActivityDraft({
            id: Date.now(),
            kind: 'settlement-phase',
            phaseId: pendingSettlementPhase || 19
        });

        if (!activitySection) {
            console.warn('Activity Feed section not found for settlement draft handoff');
            return;
        }

        const subSections = sections.filter(s => s.subsection === activitySection.id);
        applyActiveSection(activitySection.id, subSections);
    };

    const openDisengagementActivityDraft = (draft = {}) => {
        const activitySection = getActivityFeedSection();

        setDisengagementActivityDraft({
            id: Date.now(),
            kind: 'disengagement-phase',
            phaseId: draft.phase_id || pendingDisengagementPhase || 3,
            subject: draft.subject || 'Pending disengagement',
            content: draft.content || '@ksatriano Pending disengagement.',
            fieldValues: draft.field_values || []
        });

        if (!activitySection) {
            console.warn('Activity Feed section not found for disengagement draft handoff');
            return;
        }

        const subSections = sections.filter(s => s.subsection === activitySection.id);
        applyActiveSection(activitySection.id, subSections);
    };

    const openGuidedSection = (sectionName, sectionId = null) => {
        const normalizedName = String(sectionName || '').trim().toLowerCase();
        const section = (sectionId ? sections.find(s => Number(s.id) === Number(sectionId)) : null)
            || sections.find(s => String(s.name || '').trim().toLowerCase() === normalizedName);

        if (!section || !guardSettlementNavigation(section.id)) {
            return false;
        }

        const subSections = sections.filter(s => s.subsection === section.id);
        applyActiveSection(section.id, subSections);
        return true;
    };

    const openGuidedDocumentsFolder = (folderPath = '') => {
        const section = sections.find(s => String(s.name || '').trim().toLowerCase() === 'documents');
        if (!section || !guardSettlementNavigation(section.id)) {
            return false;
        }

        const subSections = sections.filter(s => s.subsection === section.id);
        applyActiveSection(section.id, subSections);

        const url = new URL(window.location.href);
        url.searchParams.set('section', String(section.id));
        url.searchParams.delete('key');
        if (folderPath) {
            url.searchParams.set('folder', folderPath);
        }
        window.history.replaceState({}, '', url);
        return true;
    };

    const openGuidedActivityDraft = (draft = {}) => {
        const activitySection = sections.find(s => String(s.name || '').trim().toLowerCase() === 'activity feed');
        if (!activitySection || !guardSettlementNavigation(activitySection.id)) {
            return false;
        }

        setGuidedActivityDraft({
            id: draft.id || Date.now(),
            type: draft.type || 'notes',
            lockedType: draft.lockedType ?? (draft.type === 'tasks' ? 1 : 0),
            subject: draft.subject || '',
            content: draft.content || '',
            lockSubject: !!draft.lockSubject,
            mentions: Array.isArray(draft.mentions) ? draft.mentions : [],
            tags: Array.isArray(draft.tags) ? draft.tags : [],
            case_id: draft.case_id || Number(id),
            lead_id: draft.lead_id || caseData?.lead?.id || null,
            post: false,
        });

        const subSections = sections.filter(s => s.subsection === activitySection.id);
        applyActiveSection(activitySection.id, subSections);
        return true;
    };

    const openGuidedBillingExpenseDraft = (draft = {}) => {
        const billingSection = sections.find(s => String(s.name || '').trim().toLowerCase() === 'time & billing');
        if (!billingSection || !guardSettlementNavigation(billingSection.id)) {
            return false;
        }

        setGuidedBillingDraft({
            id: draft.id || Date.now(),
            case_id: draft.case_id || Number(id),
            item_type: 'expense',
            entry_kind: 'expense',
            date: draft.date || new Date().toISOString().split('T')[0],
            rate: draft.rate ?? draft.amount ?? null,
            hours: draft.hours ?? draft.quantity ?? draft.raw_quantity ?? 1,
            quantity: draft.quantity ?? draft.hours ?? draft.raw_quantity ?? 1,
            raw_quantity: draft.raw_quantity ?? draft.quantity ?? draft.hours ?? 1,
            payee_name: draft.payee_name || '',
            description: draft.description || (draft.payee_name ? `Payee: ${draft.payee_name}` : 'Draft expense entry'),
            draft: true,
            billable: draft.billable ?? false,
            chargeable: draft.chargeable ?? false,
        });

        const subSections = sections.filter(s => s.subsection === billingSection.id);
        applyActiveSection(billingSection.id, subSections);
        return true;
    };

    const openGuidedSectionDataDraft = (sectionName = '', draft = {}) => {
        const normalizedName = String(sectionName || draft.section_name || '').trim().toLowerCase();
        const section = Number(draft.section_id || 0) > 0
            ? sections.find(s => Number(s.id) === Number(draft.section_id))
            : sections.find(s => String(s.name || '').trim().toLowerCase() === normalizedName);
        if (!section || !guardSettlementNavigation(section.id)) {
            return false;
        }

        setGuidedSectionDataDraft({
            id: draft.id || Date.now(),
            section_id: Number(section.id),
            section_name: section.name,
            case_id: draft.case_id || Number(id),
            values: draft.values && typeof draft.values === 'object' ? draft.values : {},
            field_intents: Array.isArray(draft.field_intents) ? draft.field_intents : [],
            description: draft.description || '',
        });

        setSectionHubMode(false);
        setSectionHubChildren([]);
        setActiveSection(section.id);
        const url = new URL(window.location.href);
        url.searchParams.set("section", section.id);
        window.history.replaceState({}, "", url);
        return true;
    };

    useEffect(() => {
        documentStructureRequestRef.current.generation += 1;
        documentStructureRequestRef.current.controller?.abort();
        documentStructureRequestRef.current.controller = null;
        const activeCaseId = normalizeDocumentRouteCaseId(id);
        folderFetchControllersRef.current.forEach((controller, requestKey) => {
            if (!activeCaseId || !String(requestKey).startsWith(`${activeCaseId}|`)) {
                controller.abort();
                folderFetchControllersRef.current.delete(requestKey);
            }
        });
        setFolders({});
        setMetaData([]);
    }, [id]);

    // Fetch documents function - defined early to avoid hoisting issues
    const fetchDocuments = useCallback(async () => {
        const caseId = caseData?.case?.case_id;
        const requestedRouteCaseId = String(id || '');
        if (!caseId || String(caseId) !== requestedRouteCaseId) return;
        documentStructureRequestRef.current.controller?.abort();
        const controller = new AbortController();
        const generation = documentStructureRequestRef.current.generation + 1;
        documentStructureRequestRef.current = { generation, controller };
        try {
            const response = await fetch(
                `https://api.casedb.co/documents.php?case_id=${caseId}&structure_only=true&t=${new Date().getTime()}`,
                { credentials: 'include', signal: controller.signal }
            );
            const data = await response.json();
            if (!documentStructureRequestIsCurrent({
                requestedCaseId: requestedRouteCaseId,
                activeCaseId: activeRouteCaseIdRef.current,
                requestGeneration: generation,
                activeGeneration: documentStructureRequestRef.current.generation,
                aborted: controller.signal.aborted
            })) {
                return;
            }
            if (data.success) {
                setFolders(data?.folders || {});
                setMetaData([]);
            }
        } catch (error) {
            if (error?.name === 'AbortError') return;
            console.error("Error fetching documents:", error);
        }
    }, [caseData?.case?.case_id, id]);

    // Handle upload service updates
    useEffect(() => {
        const unsubscribe = uploadService.addListener((currentUploads) => {
            const newCompletedCount = currentUploads.filter(u => u.status === 'completed').length;
            const prevCompletedCount = completedCountRef.current;

            // Replace {{Name}} placeholder with actual case name for display
            const caseDisplayName = caseData?.case?.case_name || caseData?.lead?.case_name || '{{Name}}';
            const mappedUploads = currentUploads.map(upload => ({
                ...upload,
                fileName: (upload.fileName || '').replace(/{{Name}}/g, caseDisplayName)
            }));
            
            setUploads(mappedUploads);
            setShowUploadProgress(currentUploads.length > 0);
            
            // If we have new completed uploads, refresh current folder
            if (newCompletedCount > prevCompletedCount) {
                console.log('Refreshing current folder after upload completion');
                if (sectionRef.current?.refreshCurrentFolder) {
                    sectionRef.current.refreshCurrentFolder();
                } else {
                    // Fallback to general document fetch if section ref not available
                    fetchDocuments();
                }
            }
            
            // Update the ref for next comparison
            completedCountRef.current = newCompletedCount;
        });

        return unsubscribe;
    }, [fetchDocuments]);

    // Global upload handler that can be called from any section
    const handleFileUpload = async (files, folderName = '') => {
        try {
            if (!files || files.length === 0) return;

            // Prepare files for upload
            const fileList = [];
            const folderPaths = new Set();
            const isFolder = files.some(file => file.webkitRelativePath && file.webkitRelativePath.includes('/'));

            Array.from(files).forEach(file => {
                const originalName = file.name;
                const relativePath = file.webkitRelativePath || originalName;
                
                const parts = relativePath.split('/').filter(p => p);
                const fileName = parts.pop();
                
                // Only collect folder paths if this is a folder upload
                if (isFolder && parts.length > 0) {
                    let currentPath = '';
                    for (let part of parts) {
                        currentPath += (currentPath ? '/' : '') + part;
                        folderPaths.add(currentPath);
                    }
                }

                fileList.push({
                    file,
                    originalName: fileName,
                    nameWithoutExt: fileName.includes('.') ? fileName.substring(0, fileName.lastIndexOf('.')) : fileName,
                    extension: fileName.includes('.') ? fileName.split('.').pop() : '',
                    newName: fileName.includes('.') ? fileName.substring(0, fileName.lastIndexOf('.')) : fileName,
                    relativePath: relativePath,
                    isFolder: isFolder
                });
            });

            // Create folders first (only for folder uploads)
            if (isFolder) {
                for (let folderPath of folderPaths) {
                    try {
                        await uploadService.createFolderOnS3({
                            caseId: id,
                            folderName: folderPath,
                            currentPath: '{{Name}}' // Always use {{Name}} as the base for folder creation
                        });
                    } catch (error) {
                        console.error(`Error creating folder ${folderPath}:`, error);
                    }
                }
            }

            // Start upload - for folder uploads, don't include the base folder name since it's in relativePath
            const uploadPromises = await uploadService.uploadFiles(fileList, {
                caseId: id,
                folderName: isFolder ? '' : (folderName || '{{Name}}'), // Empty folderName for folder uploads
                userId: user_id
            });

            // Check if any uploads failed
            const failedUploads = uploadPromises.filter(result => result.status === 'rejected');
            if (failedUploads.length > 0) {
                console.warn(`${failedUploads.length} uploads failed`, failedUploads);
            }

            // Refresh documents after upload - wait a bit for backend to process
            setTimeout(async () => {
                try {
                    // Try to fetch documents using the new approach first
                    if (fetchFolderContents) {
                        // Refresh the current folder if we're in documents section
                        const currentSection = sections.find(s => s.id === activeSection);
                        if (currentSection && currentSection.name.toLowerCase() === 'documents') {
                            // Trigger a refresh of the current folder
                            console.log('Refreshing documents after upload');
                        }
                    } else {
                        // Fallback to old method
                        fetchDocuments();
                    }
                } catch (error) {
                    console.error('Error refreshing documents after upload:', error);
                }
            }, 1500);

        } catch (error) {
            console.error('Upload error:', error);
        }
    };

    const fetchVitals = async () => {
        const scope = caseReadScope(caseData, activeRouteCaseIdRef.current);
        if (!scope?.templateId) return;
        const requestGeneration = ++vitalsRequestGenerationRef.current;
        const isCurrent = () => requestGeneration === vitalsRequestGenerationRef.current
            && scope.caseId === normalizeCaseReadId(activeRouteCaseIdRef.current)
            && scope.leadId === loadedCaseScopeRef.current?.leadId
            && scope.templateId === loadedCaseScopeRef.current?.templateId;
        try {
            const response = await fetch(`https://api.casedb.co/sections.php?vitals=true&template_id=${scope.templateId}&lead_id=${scope.leadId}&case_id=${scope.caseId}&${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            if (!isCurrent()) return;
            if (!response.ok || data?.success !== true || !Array.isArray(data?.vitals)) {
                setVitals([]);
                return;
            }
            if (data.success) {
                const currentCaseTypeId = caseData?.lead?.case_type_id;
                const filtered = (data?.vitals || []).filter((vital) => {
                    if (!vital) return false;

                    // Always include records without a saved vital row
                    if (vital.vital_id == null) return true;

                    // Global vitals: null/undefined/0 case_type_id
                    if (vital.case_type_id === null || vital.case_type_id === undefined) return true;
                    const vitalCt = parseInt(vital.case_type_id, 10);
                    if (Number.isNaN(vitalCt)) return false;
                    if (vitalCt === 0) return true;

                    // Match current case type when available
                    const currentCt = currentCaseTypeId != null ? parseInt(currentCaseTypeId, 10) : null;
                    if (currentCt === null || Number.isNaN(currentCt)) return false;
                    return vitalCt === currentCt;
                });

                setVitals(filtered);
            }
        } catch (error) {
            if (isCurrent()) setVitals([]);
            console.error("Error fetching vitals:", error);
        }
    };

    const fetchFields = async () => {
        try {
            // This will trigger re-fetching of fields in all sections when case_type_id changes
            // Individual sections will handle their own field fetching based on their section_id
            if (caseData?.lead?.case_type_id) {
                // Force refresh of vitals when fields are fetched (since vitals depend on case_type_id)
                await fetchVitals();
            }
        } catch (error) {
            console.error("Error in fetchFields:", error);
        }
    };

    // New folder-based fetch function with pagination
    const fetchFolderContents = useCallback(async (currentFolder = '', page = 1, pageSize = 100, sort = {}) => {
        let controller = null;
        let requestKey = null;
        try {
            const caseId = normalizeDocumentRouteCaseId(id);
            if (!caseId) return null;

            const normalizedSort = normalizeDocumentFolderSort(sort);

            console.log('fetchFolderContents called with:', {
                currentFolder,
                page,
                pageSize,
                caseId,
                sort: normalizedSort
            });

            requestKey = `${caseId}|${currentFolder}|${page}|${pageSize}|${normalizedSort.by}|${normalizedSort.direction}`;
            const controllers = folderFetchControllersRef.current;

            if (controllers.has(requestKey)) {
                controllers.get(requestKey).abort();
            }

            controller = new AbortController();
            controllers.set(requestKey, controller);

            const params = new URLSearchParams({
                folder_fetch: 'true',
                case_id: caseId,
                current_folder: currentFolder,
                page: page.toString(),
                page_size: pageSize.toString(),
                sort_by: normalizedSort.by,
                sort_direction: normalizedSort.direction,
                t: new Date().getTime().toString()
            });

            const url = `https://api.casedb.co/documents.php?${params}`;

            const response = await fetch(url, { credentials: 'include', signal: controller.signal });
            const data = await response.json();

            if (!documentFolderRequestIsCurrent({
                requestedCaseId: caseId,
                activeCaseId: activeRouteCaseIdRef.current,
                aborted: controller.signal.aborted
            })) {
                return null;
            }
            
            if (data.success) {
                const serverSort = data.sort && typeof data.sort === 'object'
                    ? normalizeDocumentFolderSort(data.sort)
                    : null;
                if (serverSort && !documentFolderSortsEqual(serverSort, normalizedSort)) {
                    console.warn('Ignoring folder response with mismatched sort echo', {
                        requested: normalizedSort,
                        received: serverSort
                    });
                    return null;
                }
                return {
                    // `items` is the authoritative mixed folder/file page from
                    // the new endpoint. Preserve null for an older server so
                    // DocumentSection can fall back to the legacy split lists
                    // during a server-first rolling deployment.
                    items: serverSort && Array.isArray(data.items) ? data.items : null,
                    folders: data.folders || [],
                    visibleFolders: data.visible_folders || [],
                    files: data.files || [],
                    metaData: data.metaData || [],
                    sync_info: data.sync_info || null,
                    caseTypeId: data.case_type_id ?? null,
                    pagination: data.pagination || {},
                    sort: serverSort,
                    currentFolder: data.current_folder || '',
                    folderPrefix: data.folder_prefix || ''
                };
            } else {
                console.error("Error in folder fetch response:", data.message);
                return null;
            }
        } catch (error) {
            if (error?.name === 'AbortError') {
                return null;
            }
            console.error("Error fetching folder contents:", error);
            return null;
        } finally {
            if (requestKey) {
                const controllers = folderFetchControllersRef.current;
                const existing = controllers.get(requestKey);
                if (existing && (existing.signal.aborted || existing === controller)) {
                    controllers.delete(requestKey);
                }
            }
        }
    }, [id]);

    const fetchCase = async (justData = false) => {
        const requestedRouteCaseId = normalizeCaseReadId(id);
        if (!requestedRouteCaseId) return;
        const requestGeneration = ++caseDataRequestGenerationRef.current;
        setCaseLoadError(null);
        try {
            const response = await fetch(`https://api.casedb.co/cases.php?id=${requestedRouteCaseId}`, { credentials: 'include' });
            const data = await response.json();
            if (
                requestedRouteCaseId !== String(activeRouteCaseIdRef.current || '')
                || requestGeneration !== caseDataRequestGenerationRef.current
            ) return;
            if (!response.ok || data?.success === false || !caseReadScope(data, requestedRouteCaseId)) {
                throw new Error('The case could not be loaded. It may be unavailable or you may not have access.');
            }
            setCaseData(data);

            if(!justData) {
                setPfs(data?.lead?.pfs || null);
                fetchSections(data?.case?.template_id, {
                    leadId: data?.lead?.id ?? data?.lead_id,
                    caseId: data?.case?.id ?? data?.case?.case_id,
                    caseGeneration: requestGeneration
                });
            }
        } catch (error) {
            if (
                requestedRouteCaseId === normalizeCaseReadId(activeRouteCaseIdRef.current)
                && requestGeneration === caseDataRequestGenerationRef.current
            ) {
                setCaseData({});
                setSections([]);
                setVitals([]);
                setCaseLoadError('The case could not be loaded. It may be unavailable or you may not have access.');
            }
            console.error(error);
        }
    };

    const fetchSections = async (templateId, overrides = {}) => {
        const leadId = normalizeCaseReadId(overrides.leadId ?? caseData?.lead?.id ?? caseData?.lead_id);
        const caseId = normalizeCaseReadId(overrides.caseId ?? caseData?.case?.id ?? caseData?.case?.case_id);
        const normalizedTemplateId = normalizeCaseReadId(templateId);
        if (!leadId || !caseId || caseId !== normalizeCaseReadId(activeRouteCaseIdRef.current)) return;
        const caseGeneration = overrides.caseGeneration ?? caseDataRequestGenerationRef.current;
        const requestGeneration = ++sectionsRequestGenerationRef.current;
        const isCurrent = () => caseId === normalizeCaseReadId(activeRouteCaseIdRef.current)
            && requestGeneration === sectionsRequestGenerationRef.current
            && caseGeneration === caseDataRequestGenerationRef.current;
        setSectionLoadError(null);
        try {
            if (!normalizedTemplateId) throw new Error('The case has no valid section template.');
            const params = new URLSearchParams({ template_id: normalizedTemplateId, lead_id: leadId, case_id: caseId });
            const response = await fetch(`https://api.casedb.co/sections.php?${params.toString()}`, { credentials: 'include' });
            const data = await response.json();
            if (!isCurrent()) return;
            const nextSections = caseSectionsFromResponse(data);
            if (!response.ok || nextSections === null) throw new Error('The case sections could not be loaded.');
            setSections(nextSections);
        } catch (error) {
            if (isCurrent()) {
                setSections([]);
                setSectionLoadError('The case sections could not be loaded. Please retry; if this continues, check case access.');
            }
            console.error(error);
        }
    };

    const fetchCaseTypes = async () => {
        try {
            const response = await fetch("https://api.casedb.co/case-types.php");
            const data = await response.json();
            if (response.ok && Array.isArray(data?.case_types)) {
                setCaseTypes(data.case_types.filter(ct => ct && typeof ct.name === 'string' && ct.name !== "All"));
            }
        } catch (err) {
            console.error(err);
        }
    }

    // Redirect to /cases if case ID is null or undefined
    useEffect(() => {
        if (id === 'null' || id === 'undefined' || !id) {
            console.warn('Invalid case ID detected, redirecting to /cases');
            navigate('/cases', { replace: true });
        }
    }, [id, navigate]);

    useEffect(() => {
        setCaseData({});
        setSections([]);
        setVitals([]);
        setCaseLoadError(null);
        setSectionLoadError(null);
        setSectionHubMode(false);
        setSectionHubChildren([]);
        sectionsRequestGenerationRef.current += 1;
        vitalsRequestGenerationRef.current += 1;
        if (id && id !== 'null' && id !== 'undefined') {
            fetchCase();
        }
        return () => {
            caseDataRequestGenerationRef.current += 1;
            sectionsRequestGenerationRef.current += 1;
            vitalsRequestGenerationRef.current += 1;
        };
    }, [id]);

    useEffect(() => {
        fetchVitals();
    }, [caseData, id]);

    useEffect(() => { fetchCaseTypes(); }, []);

    useEffect(() => {
        const caseTitle = caseData?.case?.case_name || caseData?.lead?.case_name;
        const sectionTitle = sections.find(s => s.id === activeSection)?.name;
        const titleParts = [caseTitle, sectionTitle].filter(Boolean);
        document.title = titleParts.length > 0 ? `${titleParts.join(' | ')} – CaseDB` : 'CaseDB';
        return () => {
            document.title = 'CaseDB';
        };
    }, [caseData?.case?.case_name, caseData?.lead?.case_name, sections, activeSection]);

    const parentSection =
        sectionHubChildren?.length > 0
            ? sections?.find(s => s?.id === sectionHubChildren[0]?.subsection)
            : sections?.find(s => s?.id === activeSection);

    // Listen for settlement navigation event
    useEffect(() => {
        const handleSettlementNavigation = (event) => {
            if (sections.length > 0) {
                // Extract pending phase from event details if available
                const pendingPhase = event.detail?.pendingPhase;
                if (pendingPhase) {
                    setPendingSettlementPhase(pendingPhase);
                    setSettlementFieldsRequired(true);
                }
                // Navigate to section 20 first to fill settlement fields, starting with Made Whole?
                navigate('?section=20#Fee-Arrangement', { replace: true });
            }
        };

        window.addEventListener('navigateToSettlement', handleSettlementNavigation);
        return () => window.removeEventListener('navigateToSettlement', handleSettlementNavigation);
    }, [sections, navigate]);

    useEffect(() => {
        const handleSettlementVerificationNotice = (event) => {
            const message = event.detail?.message || 'Settlement fields verified. Please confirm to proceed.';
            setSettlementVerificationNotice({
                id: Date.now(),
                message
            });
        };

        window.addEventListener('settlementVerificationNotice', handleSettlementVerificationNotice);
        return () => window.removeEventListener('settlementVerificationNotice', handleSettlementVerificationNotice);
    }, []);

    useEffect(() => {
        const handleDisengagementNavigation = (event) => {
            if (sections.length > 0) {
                const pendingPhase = event.detail?.pendingPhase || 3;
                setPendingDisengagementPhase(pendingPhase);
                setDisengagementFieldsRequired(true);
                navigate('?section=20', { replace: true });
            }
        };

        window.addEventListener('navigateToDisengagement', handleDisengagementNavigation);
        return () => window.removeEventListener('navigateToDisengagement', handleDisengagementNavigation);
    }, [sections, navigate]);

    useEffect(() => {
        const handleDisengagementActivityNavigation = (event) => {
            if (sections.length > 0) {
                const draft = event.detail?.draft || {};
                const pendingPhase = event.detail?.pendingPhase || draft.phase_id || 3;
                setPendingDisengagementPhase(pendingPhase);
                setDisengagementFieldsRequired(false);
                openDisengagementActivityDraft(draft);
            }
        };

        window.addEventListener('navigateToDisengagementActivity', handleDisengagementActivityNavigation);
        return () => window.removeEventListener('navigateToDisengagementActivity', handleDisengagementActivityNavigation);
    }, [sections, openDisengagementActivityDraft]);

    // Listen for navigation to verification section after settlement fields are saved
    useEffect(() => {
        const handleNavigateToVerification = (event) => {
            console.log('handleNavigateToVerification event received:', event.detail);
            console.log('sections:', sections.length);
            
            if (sections.length > 0) {
                const sectionId = event.detail?.sectionId || 24;
                const pendingPhase = event.detail?.pendingPhase;
                
                console.log('Navigating to section:', sectionId, 'with pendingPhase:', pendingPhase);
                
                // If pendingPhase is provided, set it (this may be a re-trigger)
                if (pendingPhase) {
                    console.log('Setting pending settlement phase:', pendingPhase);
                    setPendingSettlementPhase(pendingPhase);
                    setSettlementFieldsRequired(false);
                }
                
                // Always navigate, even if we're already in section 24 (allows re-triggering)
                navigate(`?section=${sectionId}`, { replace: true });
            } else {
                console.warn('Sections not loaded yet, cannot navigate to verification');
            }
        };

        window.addEventListener('navigateToVerification', handleNavigateToVerification);
        return () => window.removeEventListener('navigateToVerification', handleNavigateToVerification);
    }, [sections, navigate]);

    // Listen for settlement state clear event after successful phase update
    useEffect(() => {
        const handleClearSettlementState = () => {
            setPendingSettlementPhase(null);
            setSettlementFieldsRequired(false);
            setSettlementVerificationNotice(null);
            setSettlementActivityDraft(null);
        };

        window.addEventListener('clearSettlementState', handleClearSettlementState);
        return () => window.removeEventListener('clearSettlementState', handleClearSettlementState);
    }, []);

    useEffect(() => {
        const handleClearDisengagementState = () => {
            setPendingDisengagementPhase(null);
            setDisengagementFieldsRequired(false);
            setDisengagementActivityDraft(null);
        };

        window.addEventListener('clearDisengagementState', handleClearDisengagementState);
        return () => window.removeEventListener('clearDisengagementState', handleClearDisengagementState);
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sectionParam = params.get("section");
        const keyParam = params.get("key");
        const generationGroupParam = params.get("group");
        const generationReviewParam = params.get("generation_review");

        if (generationReviewParam === '1' && sectionParam && location.hash) {
            const fieldMatch = location.hash.match(/^#field-(\d+)$/);
            const targetSectionId = Number(sectionParam);
            const targetFieldId = Number(fieldMatch?.[1] || 0);
            if (targetSectionId > 0 && targetFieldId > 0) {
                setGenerationEditTarget(previous => (
                    Number(previous?.sectionId) === targetSectionId
                    && Number(previous?.fieldId) === targetFieldId
                    && String(previous?.groupId ?? '') === String(generationGroupParam ?? '')
                        ? previous
                        : {
                            sectionId: targetSectionId,
                            fieldId: targetFieldId,
                            groupId: generationGroupParam === null ? null : Number(generationGroupParam),
                            token: Date.now()
                        }
                ));
            }
        }

        const activitySection = getActivityFeedSection();
        const disengagementAllowedSection = sectionParam === '20' || (activitySection && sectionParam === String(activitySection.id));
        const settlementAllowedSection = sectionParam === '20' || sectionParam === '24';
        if (sectionParam && ((isSettlementFlowActive && !settlementAllowedSection) || (isDisengagementFlowActive && !disengagementAllowedSection))) {
            const confirmNavigate = window.confirm(
                isDisengagementFlowActive
                    ? 'Disengagement phase is in progress. Please save/confirm before leaving. Continue to the required section?'
                    : 'Settlement phase is in progress. Please save/confirm before leaving. Continue to the required section?'
            );

            if (confirmNavigate) {
                if (isSettlementFlowActive) clearSettlementFlow();
                if (isDisengagementFlowActive) clearDisengagementFlow();
            } else {
                const redirectSection = isDisengagementFlowActive ? 20 : getSettlementRedirectSection();
                navigate(`?section=${redirectSection}`, { replace: true });
                return;
            }
        }
        
        // Store the key parameter in state if it exists
        if (keyParam) {
            setActivityKey(keyParam);

            if (sections.length > 0) {
                const activitySection = sections.find(s => s.name?.toLowerCase() === 'activity feed');
                const sectionMatchesActivity = activitySection && (
                    sectionParam === String(activitySection.id) ||
                    (sectionParam && sectionParam.toLowerCase() === 'activity feed')
                );

                if (activitySection && !sectionMatchesActivity) {
                    const subSections = sections.filter(s => s.subsection === activitySection.id);
                    handleSetActiveSection(activitySection.id, subSections);
                    return;
                }
            }
        } else if (activityKey !== null) {
            setActivityKey(null);
        }

        if (sectionParam && sections.length > 0) {
            const sectionId = parseInt(sectionParam, 10);
            const foundSectionById = !isNaN(sectionId)
                ? sections.find(s => s.id === sectionId)
                : null;

            if (foundSectionById) {
                const subSections = sections.filter(s => s.subsection === foundSectionById.id);
                handleSetActiveSection(foundSectionById.id, subSections);
                
                // Scroll to hash if present in URL
                if (location.hash && !generationGroupParam && generationReviewParam !== '1') {
                    setTimeout(() => {
                        const element = document.getElementById(location.hash.substring(1));
                        if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            
                            // Remove hash from URL after scrolling
                            navigate(location.pathname + location.search, { replace: true });
                        }
                    }, 500);
                }
                return;
            }

            const foundByName = sections.find(s =>
                s.name.toLowerCase() === sectionParam.toLowerCase()
            );

            if (foundByName) {
                const subSections = sections.filter(s => s.subsection === foundByName.id);
                handleSetActiveSection(foundByName.id, subSections);
                return;
            }
        }

        const parentSection = sections.find(
            s => s.id === sectionHubChildren[0]?.subsection
        );

        if (sections.length > 0 && !sectionParam) {
            const first = sections[0];
            const subSections = sections.filter(s => s.subsection === first.id);
            handleSetActiveSection(first.id, subSections);
        }
    }, [sections, location.search, location.hash]); // eslint-disable-line react-hooks/exhaustive-deps

    const staticSections = [
        'Documents',
        'Intake Details',
        'Activity Feed',
        'Time & Billing',
        'Team',
        'Related Cases',
        'Contacts',
        'Deadline Chain'
    ];

    const handleCloseUploadProgress = () => {
        uploadService.clearCompleted();
        const remainingUploads = uploadService.getUploads();
        if (remainingUploads.length === 0) {
            setShowUploadProgress(false);
        }
    };

    const handleResumeUpload = async (uploadId) => {
        const upload = uploads.find(u => u.id === uploadId);
        if (!upload || !upload.canResume) return;

        try {
            uploadService.removeUpload(uploadId);
        } catch (error) {
            console.error('Resume upload error:', error);
        }
    };

    // Handle vital updates - refresh section fields if no unsaved changes
    const handleVitalUpdate = ({ field, newValue, table }) => {
        console.log('Vital updated:', { field, newValue, table });
        console.log('sectionRef.current:', sectionRef.current);
        console.log('sectionRef.current?.refreshFields:', sectionRef.current?.refreshFields);
        
        // Check if the Section component is mounted and has the refreshFields method
        if (sectionRef.current?.refreshFields) {
            console.log('Calling refreshFields from Section ref');
            sectionRef.current.refreshFields();
        } else {
            console.log('Cannot call refreshFields - ref not available');
            console.log('   - sectionRef exists:', !!sectionRef.current);
            console.log('   - refreshFields exists:', !!sectionRef.current?.refreshFields);
        }
    };

    useEffect(() => {
        const applyWorkspaceAction = async (action) => {
            if (!action) return;

            switch (action.type) {
                case 'open_case_section':
                    openGuidedSection(action.section_name, action.section_id);
                    break;
                case 'open_documents_folder':
                    openGuidedDocumentsFolder(action.folder_path || '');
                    break;
                case 'open_activity_feed_draft':
                    openGuidedActivityDraft(action.draft || {});
                    break;
                case 'open_billing_expense_draft':
                    openGuidedBillingExpenseDraft(action.draft || {});
                    break;
                case 'open_section_data_draft':
                    openGuidedSectionDataDraft(action.section_name, {
                        ...(action.draft || {}),
                        section_id: Number(action.section_id || action.draft?.section_id || 0) || undefined,
                    });
                    break;
                default:
                    break;
            }
        };

        const executePlan = async (plan) => {
            if (!plan || Number(plan.caseId) !== Number(id) || sections.length === 0) {
                return false;
            }

            for (const action of Array.isArray(plan.actions) ? plan.actions : []) {
                await applyWorkspaceAction(action);
            }

            if (typeof window !== 'undefined') {
                window.sessionStorage.removeItem(JANE_WORKSPACE_PLAN_STORAGE_KEY);
            }

            return true;
        };

        const handleJaneWorkspacePlan = (event) => {
            const plan = event.detail || null;
            if (!plan || Number(plan.caseId) !== Number(id)) {
                return;
            }

            if (sections.length === 0) {
                setPendingJaneWorkspacePlan(plan);
                return;
            }

            executePlan(plan);
        };

        const consumeStoredJaneWorkspacePlan = () => {
            if (typeof window === 'undefined' || sections.length === 0) return;
            const rawPlan = window.sessionStorage.getItem(JANE_WORKSPACE_PLAN_STORAGE_KEY);
            if (!rawPlan) return;
            try {
                const plan = JSON.parse(rawPlan);
                if (Number(plan?.caseId) === Number(id)) {
                    executePlan(plan);
                }
            } catch (error) {
                window.sessionStorage.removeItem(JANE_WORKSPACE_PLAN_STORAGE_KEY);
            }
        };

        consumeStoredJaneWorkspacePlan();

        window.addEventListener('janeWorkspacePlan', handleJaneWorkspacePlan);
        return () => window.removeEventListener('janeWorkspacePlan', handleJaneWorkspacePlan);
    }, [id, sections, openGuidedSection, openGuidedDocumentsFolder, openGuidedActivityDraft, openGuidedBillingExpenseDraft, openGuidedSectionDataDraft]);

    useEffect(() => {
        if (!pendingJaneWorkspacePlan || Number(pendingJaneWorkspacePlan.caseId) !== Number(id) || sections.length === 0) {
            return;
        }

        for (const action of Array.isArray(pendingJaneWorkspacePlan.actions) ? pendingJaneWorkspacePlan.actions : []) {
            if (action.type === 'open_case_section') {
                openGuidedSection(action.section_name, action.section_id);
            } else if (action.type === 'open_documents_folder') {
                openGuidedDocumentsFolder(action.folder_path || '');
            } else if (action.type === 'open_activity_feed_draft') {
                openGuidedActivityDraft(action.draft || {});
            } else if (action.type === 'open_billing_expense_draft') {
                openGuidedBillingExpenseDraft(action.draft || {});
            } else if (action.type === 'open_section_data_draft') {
                openGuidedSectionDataDraft(action.section_name, {
                    ...(action.draft || {}),
                    section_id: Number(action.section_id || action.draft?.section_id || 0) || undefined,
                });
            }
        }

        setPendingJaneWorkspacePlan(null);
    }, [pendingJaneWorkspacePlan, id, sections, openGuidedSection, openGuidedDocumentsFolder, openGuidedActivityDraft]);

    const handleGenerationEditTarget = (target) => {
        const sectionId = Number(target?.sectionId || 0);
        const fieldId = Number(target?.fieldId || 0);
        if (!sectionId || !fieldId || !guardSettlementNavigation(sectionId)) return false;

        const section = sections.find(item => Number(item.id) === sectionId);
        if (!section) return false;

        const subSections = sections.filter(item => Number(item.subsection) === sectionId);
        applyActiveSection(sectionId, subSections);
        setGenerationEditTarget(target);

        const params = new URLSearchParams(location.search);
        params.set('section', String(sectionId));
        params.set('generation_review', '1');
        if (target.groupId !== null && target.groupId !== undefined) {
            params.set('group', String(target.groupId));
        } else {
            params.delete('group');
        }
        navigate(`${location.pathname}?${params.toString()}#field-${fieldId}`, { replace: true });
        return true;
    };

    const handleGenerationEditTargetConsumed = () => {
        setGenerationEditTarget(null);
        const params = new URLSearchParams(location.search);
        params.delete('group');
        params.delete('generation_review');
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    };

    return (
      <GenerationReviewProvider
        caseId={id}
        onEditTarget={handleGenerationEditTarget}
        onGenerated={(result, config) => {
            if (!config?.onGenerated
                && config?.engine === 'docgen'
                && Number(config?.source?.groupId || 0) > 0
            ) {
                handleGenerationEditTarget({
                    sectionId: Number(config?.source?.sectionId || 0),
                    fieldId: Number(config?.source?.fieldId || 0),
                    groupId: Number(config.source.groupId),
                    token: Date.now()
                });
            }
            sectionRef.current?.refreshFields?.();
        }}
      >
        <div className={`case-container ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
            {screenWidth >= 768 && 
                <CaseNav 
                    sections={sections} 
                    activeSection={activeSection} 
                    setActiveSection={handleSetActiveSection} 
                    pfs={pfs}
                    caseType={caseData?.lead?.case_type_id}
                />
            }
            <div className='case-body'>
                <CaseHeader 
                    ref={caseHeaderRef}
                    caseData={caseData} 
                    fetchCase={fetchCase} 
                    vitals={vitals} 
                    fetchVitals={fetchVitals} 
                    caseTypes={caseTypes} 
                    fetchFields={fetchFields} 
                    openVitals={openVitals} 
                    setOpenVitals={setOpenVitals}
                    onVitalUpdate={handleVitalUpdate}
                    onSettlementFieldsRequiredChange={setSettlementFieldsRequired}
                    onDisengagementFieldsRequiredChange={setDisengagementFieldsRequired}
                    onFetchPhases={(phases, subphases) => {
                        // This callback is triggered when CaseHeader fetches phases
                        // Just here for future use if needed
                    }}
                />
                {screenWidth < 768 && 
                    <CaseNav
                        key={caseData?.lead?.id}
                        sections={sections} 
                        activeSection={activeSection} 
                        setActiveSection={handleSetActiveSection} 
                        pfs={pfs}
                        horizontal={true}
                        caseType={caseData?.lead?.case_type_id}
                    />
                }
                {!loadedCaseScope ? (
                    <div className="case-section" role={caseLoadError ? 'alert' : 'status'}>
                        <p>{caseLoadError || 'Loading case…'}</p>
                        {caseLoadError && <button type="button" onClick={() => fetchCase()}>Retry loading case</button>}
                    </div>
                ) : sectionLoadError ? (
                    <div className="case-section" role="alert">
                        <p>{sectionLoadError}</p>
                        <button type="button" onClick={() => fetchSections(caseData?.case?.template_id)}>Retry loading sections</button>
                    </div>
                ) : sectionHubMode ? (
                    <SectionHub 
                        key={activeSection}
                        parentSectionId={activeSection} 
                        subSections={sectionHubChildren} 
                        caseId={id}
                        user_id={user_id}
                        activeSection={sections.find(s => s.id === activeSection)}
                        setActiveSection={handleSetActiveSection}
                    />
                ) : (
                    <Section 
                        ref={sectionRef}
                        key={activeSection} 
                        user_id={user_id} 
                        lead_id={caseData?.lead?.id} 
                        id={id} 
                        caseName={caseData?.case?.case_name} 
                        fetchDocuments={fetchDocuments} 
                        fetchFolderContents={fetchFolderContents}
                        folders={folders} 
                        metaData={metaData} 
                        caseType={caseData?.lead?.case_type_id}
                        caseTypes={caseTypes}
                        section_id={activeSection} 
                        template_id={caseData?.case?.template_id} 
                        caseData={caseData} 
                        fetchCase={fetchCase}
                        onFileUpload={handleFileUpload}
                        refetchFields={fetchFields}
                        activityKey={activityKey}
                        documentLiveUnreadRegistry={documentLiveUnreadHolderRef.current.registry}
                        openVitals={openVitals}
                        vitals={vitals}
                        fetchVitals={fetchVitals}
                        settlementFieldsRequired={settlementFieldsRequired}
                        pendingSettlementPhase={pendingSettlementPhase}
                        settlementVerificationNotice={settlementVerificationNotice}
                        settlementActivityDraft={settlementActivityDraft}
                        disengagementFieldsRequired={disengagementFieldsRequired}
                        pendingDisengagementPhase={pendingDisengagementPhase}
                        disengagementActivityDraft={disengagementActivityDraft}
                        guidedActivityDraft={guidedActivityDraft}
                        guidedBillingDraft={guidedBillingDraft}
                        guidedSectionDataDraft={guidedSectionDataDraft}
                        generationEditTarget={generationEditTarget}
                        sections={sections}
                        onSettlementDraftConsumed={() => setSettlementActivityDraft(null)}
                        onDisengagementDraftConsumed={() => setDisengagementActivityDraft(null)}
                        onGuidedDraftConsumed={() => setGuidedActivityDraft(null)}
                        onGuidedBillingDraftConsumed={() => setGuidedBillingDraft(null)}
                        onGuidedSectionDataDraftConsumed={() => setGuidedSectionDataDraft(null)}
                        onGenerationEditTargetConsumed={handleGenerationEditTargetConsumed}
                        onSettlementPhaseConfirmed={() => {
                            openSettlementActivityDraft();
                        }}
                        onSettlementDraftSubmitted={() => {
                            setPendingSettlementPhase(null);
                            setSettlementFieldsRequired(false);
                            setSettlementVerificationNotice(null);
                            setSettlementActivityDraft(null);
                        }}
                        onDisengagementDraftSubmitted={() => {
                            setPendingDisengagementPhase(null);
                            setDisengagementFieldsRequired(false);
                            setDisengagementActivityDraft(null);
                            fetchCase(true);
                        }}
                        onSettlementDraftCancelled={clearSettlementFlow}
                        onDisengagementDraftCancelled={clearDisengagementFlow}
                        onPhasesSynced={() => {
                            // Callback when phases need to be synced
                            // Fetch full case data to update phases/subphases info
                            fetchCase(true);
                        }}
                    />
                )}
            </div>
        </div>
      </GenerationReviewProvider>
    );
};

export default Case;
